import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  fetchCampusDetails,
  fetchChildrenByCampus,
} from "../services/childDataService";
import { createPhoto } from "../services/photoService";
import { uploadPhotoVersion, validateFile } from "../services/storageService";
import type {
  Campus,
  Child,
  FileUploadObj,
  FileUploadProgress,
} from "../types";
import {
  generateFileId,
  uploadWithConcurrency,
  validateAndFilterFiles,
  formatSuccessMessage,
  formatErrorMessage,
} from "../utils/uploadUtils";
import { generateThumbHash } from "../utils/thumbhashUtils";
import { useWatermark } from "./useWatermark";

// Extended file object to store both original and watermarked files
interface ExtendedFileUploadObj extends FileUploadObj {
  originalFile?: File; // Store original file for upload
  watermarkedFile?: File; // Store watermarked file for upload
}

export interface UsePhotoUploadReturn {
  // Data
  campusId: string | undefined;
  selectedChild: string;
  uploadedFiles: ExtendedFileUploadObj[];
  children: Child[];
  campusDetails: Campus | null;
  selectedDate: string;
  searchTerm: string;
  uploadProgress: Record<string, FileUploadProgress>;
  overallProgress: { current: number; total: number };
  showGallery: boolean;

  // States
  uploading: boolean;
  loading: boolean;
  error: string;
  success: string;

  // Actions
  setSelectedChild: (childId: string) => void;
  setUploadedFiles: React.Dispatch<
    React.SetStateAction<ExtendedFileUploadObj[]>
  >;
  setSelectedDate: (date: string) => void;
  setSearchTerm: (term: string) => void;
  setShowGallery: (show: boolean) => void;
  setError: (error: string) => void;
  setSuccess: (success: string) => void;

  // File operations
  onDrop: (acceptedFiles: File[]) => void;
  removeFile: (fileId: string) => void;
  uploadFiles: (folderName: string, folderId?: string) => Promise<void>;

  // Computed
  filteredChildren: Child[];
}

export const usePhotoUpload = (): UsePhotoUploadReturn => {
  const { centerId: campusId } = useParams<{ centerId: string }>();
  const { currentUser } = useAuth(); // Get current user for uploadedBy field

  // Initialize watermark hook
  const { addImageWatermark } = useWatermark();

  // State variables
  const [selectedChild, setSelectedChild] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<ExtendedFileUploadObj[]>(
    []
  );
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [children, setChildren] = useState<Child[]>([]);
  const [campusDetails, setCampusDetails] = useState<Campus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [uploadProgress, setUploadProgress] = useState<
    Record<string, FileUploadProgress>
  >({});
  const [overallProgress, setOverallProgress] = useState<{
    current: number;
    total: number;
  }>({ current: 0, total: 0 });
  const [showGallery, setShowGallery] = useState<boolean>(false);

  // Load campus data on mount
  useEffect(() => {
    const loadCampusData = async () => {
      try {
        setLoading(true);
        const [childrenData, campusData] = await Promise.all([
          fetchChildrenByCampus(campusId!),
          fetchCampusDetails(campusId!),
        ]);
        setChildren(childrenData);
        setCampusDetails(campusData);
      } catch (error) {
        console.error("Error loading campus data:", error);
        setError("Failed to load campus data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (campusId) {
      loadCampusData();
    }
  }, [campusId]);

  // Handle file drop
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      // Validate files
      const validFiles = validateAndFilterFiles(acceptedFiles, validateFile, setError);

      try {
        // Process files with watermarks
        const processedFiles = await Promise.all(
          validFiles.map(async (file) => {
            try {
              // Add watermark image to the photo
              const watermarkedFile = await addImageWatermark(file, {
                position: "bottom-right",
                opacity: 0.8,
                padding: 30,
              });

              return {
                file: watermarkedFile, // Display watermarked preview
                originalFile: file, // Store original for upload
                watermarkedFile: watermarkedFile, // Store watermarked for upload
                id: generateFileId(),
                preview: URL.createObjectURL(watermarkedFile),
                uploading: false,
                uploaded: false,
              };
            } catch (watermarkError) {
              console.error(
                "Watermarking failed for file:",
                file.name,
                watermarkError
              );
              // Fallback to original file if watermarking fails
              return {
                file,
                originalFile: file,
                watermarkedFile: file, // Use original as fallback
                id: generateFileId(),
                preview: URL.createObjectURL(file),
                uploading: false,
                uploaded: false,
              };
            }
          })
        );

        setUploadedFiles((prev) => [...prev, ...processedFiles]);
      } catch (error) {
        console.error("Error processing files:", error);
        setError("Failed to process images. Please try again.");
      }
    },
    [addImageWatermark]
  );

  // Remove file from upload list
  const removeFile = useCallback((fileId: string): void => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  // Filter children based on search term
  const filteredChildren = children.filter(
    (child) =>
      child.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      child.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      child.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      child.childID?.toString().includes(searchTerm)
  );

  /**
   * Upload a single file and update its progress
   */
  const uploadSingleFile = async (
    fileObj: ExtendedFileUploadObj,
    folderName: string = "",
    folderId: string = ""
  ): Promise<boolean> => {
    if (!currentUser || !campusId) return false;

    const selectedChildData = children.find(
      (child) => child.childID === selectedChild
    );
    if (!selectedChildData) return false;

    const childId = selectedChildData.childID;

    try {
      // Update progress
      setUploadProgress((prev) => ({
        ...prev,
        [fileObj.id]: { uploading: true, progress: 0 },
      }));

      // Generate UUID for this photo
      const photoId = crypto.randomUUID();

      // Upload preview version (watermarked) with progress tracking
      const previewResult = await uploadPhotoVersion(
        fileObj.watermarkedFile || fileObj.file,
        campusId,
        childId,
        photoId,
        "preview",
        (progress) => {
          // Map 0-100% to 0-50%
          const adjustedProgress = progress * 0.5;
          setUploadProgress((prev) => ({
            ...prev,
            [fileObj.id]: { uploading: true, progress: adjustedProgress },
          }));
        }
      );

      // Upload original version (untouched) with progress tracking
      const originalResult = await uploadPhotoVersion(
        fileObj.originalFile || fileObj.file,
        campusId,
        childId,
        photoId,
        "original",
        (progress) => {
          // Map 0-100% to 50-100%
          const adjustedProgress = 50 + progress * 0.5;
          setUploadProgress((prev) => ({
            ...prev,
            [fileObj.id]: { uploading: true, progress: adjustedProgress },
          }));
        }
      );

      // Generate thumbhash from original file
      const thumbhash = await generateThumbHash(
        fileObj.originalFile || fileObj.file
      );

      // Create Firestore record with both URLs
      await createPhoto({
        folderName: folderName || selectedDate,
        ...(folderId && { folderId }),
        childId,
        uploadedBy: currentUser.uid,
        originalUrl: originalResult.downloadURL,
        previewUrl: previewResult.downloadURL,
        metadata: {
          uploadDate: selectedDate,
          ...(folderId && { folderId }),
          originalFileName:
            fileObj.originalFile?.name || fileObj.file.name,
          campusId: campusId,
          fileSize: originalResult.size,
          thumbhash,
        },
      });

      // Mark as uploaded
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileObj.id
            ? {
                ...f,
                uploaded: true,
                downloadURL: previewResult.downloadURL,
                storagePath: previewResult.storagePath,
                fileName: previewResult.fileName,
              }
            : f
        )
      );

      // Update progress to completed
      setUploadProgress((prev) => ({
        ...prev,
        [fileObj.id]: { uploading: false, progress: 100, completed: true },
      }));

      return true;
    } catch (err) {
      console.error("Upload failed for file:", fileObj.file.name, err);
      setUploadProgress((prev) => ({
        ...prev,
        [fileObj.id]: { uploading: false, error: true },
      }));
      return false;
    }
  };

  // Upload files function with Firestore integration
  const uploadFiles = useCallback(
    async (folderName: string = "", folderId: string = ""): Promise<void> => {
      if (!selectedChild) {
        setError("Please select a child first");
        return;
      }

      if (uploadedFiles.length === 0) {
        setError("Please select files to upload");
        return;
      }

      if (!currentUser) {
        setError("Authentication required. Please log in again.");
        return;
      }

      if (!campusId) {
        setError("Campus ID not found");
        return;
      }

      setUploading(true);
      setError("");
      setSuccess("");
      setUploadProgress({});
      setOverallProgress({ current: 0, total: 0 });

      try {
        // Get the selected child data
        const selectedChildData = children.find(
          (child) => child.childID === selectedChild
        );
        if (!selectedChildData) {
          throw new Error("Selected child data not found");
        }

        // Split files that need uploading from already uploaded ones
        const filesToUpload = uploadedFiles.filter((f) => !f.uploaded);
        const alreadyUploaded = uploadedFiles.filter((f) => f.uploaded);

        // Count already uploaded files as success
        const preUploadedCount = alreadyUploaded.length;

        // Use concurrency utility for uploading with progress tracking
        const { successCount: newSuccessCount, errorCount } =
          await uploadWithConcurrency({
            items: filesToUpload,
            maxConcurrent: 3,
            uploadFn: (file) =>
              uploadSingleFile(file, folderName, folderId),
            onProgress: (current) => {
              setOverallProgress({
                current: preUploadedCount + current,
                total: uploadedFiles.length,
              });
            },
          });

        const successCount = preUploadedCount + newSuccessCount;

        if (successCount > 0) {
          setSuccess(formatSuccessMessage(newSuccessCount, "photo"));

          // Clear files and show gallery after successful upload
          setTimeout(() => {
            setUploadedFiles([]);
            setUploadProgress({});
            setOverallProgress({ current: 0, total: 0 });
            setSuccess("");
            setShowGallery(true);
          }, 3000);
        }

        if (errorCount > 0) {
          setError(formatErrorMessage(errorCount));
        }
      } catch (error) {
        setError("Failed to upload photos. Please try again.");
        console.error("Upload error:", error);
      }

      setUploading(false);
    },
    [
      selectedChild,
      uploadedFiles,
      children,
      campusId,
      selectedDate,
      currentUser,
    ]
  );

  return {
    // Data
    campusId,
    selectedChild,
    uploadedFiles,
    children,
    campusDetails,
    selectedDate,
    searchTerm,
    uploadProgress,
    overallProgress,
    showGallery,

    // States
    uploading,
    loading,
    error,
    success,

    // Actions
    setSelectedChild,
    setUploadedFiles,
    setSelectedDate,
    setSearchTerm,
    setShowGallery,
    setError,
    setSuccess,

    // File operations
    onDrop,
    removeFile,
    uploadFiles,

    // Computed
    filteredChildren,
  };
};
